import { Link } from 'react-router-dom';
import type { RGB } from '../types';
import { getColorReference } from '../data/colorCatalog';
import { COLOR_COMPARISONS } from '../data/colorComparisons';
import { COLOR_COLLECTION_LINKS, PALETTE_COLLECTIONS } from '../data/paletteCollections';
import { rgbToHex, rgbToHsl } from '../utils/colors';
import { getContrastRatio } from '../utils/colorAnalysis';
import { getComplementaryColor } from '../utils/colorHarmony';

interface ColorReferenceContentProps {
  hex: string;
  rgb: RGB;
}

export default function ColorReferenceContent({ hex, rgb }: ColorReferenceContentProps) {
  const reference = getColorReference(hex);
  if (!reference) return null;

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const achromatic = rgb.r === rgb.g && rgb.g === rgb.b;
  const complement = getComplementaryColor(rgb);
  const complementHex = rgbToHex(complement.r, complement.g, complement.b).slice(1);
  const textChoices = [
    { name: 'Black', hex: '000000', ratio: getContrastRatio(reference.hex, '000000') },
    { name: 'White', hex: 'FFFFFF', ratio: getContrastRatio(reference.hex, 'FFFFFF') },
  ];
  const palette = [...new Set([reference.hex, ...(achromatic ? [] : [complementHex]), 'FFFFFF', '000000'])];
  const related = COLOR_COMPARISONS.filter(comparison => comparison.first.hex === reference.hex || comparison.second.hex === reference.hex);
  const collectionLink = COLOR_COLLECTION_LINKS[reference.hex];
  const collection = collectionLink ? PALETTE_COLLECTIONS.find(item => item.slug === collectionLink.slug) : undefined;

  return (
    <section className="ui-panel p-5 sm:p-6" aria-labelledby="color-reference-heading">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 id="color-reference-heading" className="text-xl font-bold">{reference.name} color reference</h2>
        <p className="text-xs text-ink-muted">Updated <time dateTime={reference.updated}>September 13, 2026</time></p>
      </div>
      <p className="mt-3 leading-relaxed text-ink-secondary">{reference.note}</p>
      <p className="mt-3 text-sm leading-relaxed text-ink-secondary">
        {reference.cssNames.length
          ? <>CSS keyword{reference.cssNames.length > 1 ? 's' : ''}: <strong className="text-ink">{reference.cssNames.join(', ')}</strong>. Each resolves to #{reference.hex} in sRGB.</>
          : <>This descriptive name identifies our chosen sRGB swatch, not a CSS keyword or a universal material standard.</>}
      </p>

      <dl className="mt-5 grid sm:grid-cols-3 gap-4 rounded-xl bg-surface-sunken p-4 text-sm">
        <div><dt className="font-semibold text-ink-secondary">HEX — exact</dt><dd className="mt-1 font-mono">#{reference.hex}</dd></div>
        <div><dt className="font-semibold text-ink-secondary">RGB — exact 8-bit channels</dt><dd className="mt-1 font-mono">{rgb.r}, {rgb.g}, {rgb.b}</dd></div>
        <div><dt className="font-semibold text-ink-secondary">HSL — rounded</dt><dd className="mt-1 font-mono">{achromatic ? 'none' : `${hsl.h}°`}, {hsl.s}%, {hsl.l}%</dd></div>
      </dl>

      <div className="mt-6">
        <h3 className="font-bold">What is the complementary color?</h3>
        <p className="mt-2 text-sm leading-relaxed text-ink-secondary">
          {achromatic
            ? 'This neutral has no hue, so it has no unique hue-wheel complement. Build a neutral palette by changing lightness and testing the text pairs.'
            : <>An HSL hue rotation of 180° gives <Link className="font-mono text-brand-700 underline underline-offset-4" to={`/color/${complementHex}`}>#{complementHex}</Link>, keeping the rounded saturation and lightness values. This reference calculation rounds HSL before converting back to RGB. It is a digital harmony starting point, not a pigment-mixing rule or a guarantee of readable text.</>}
        </p>
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium">
          <Link className="text-brand-700 underline underline-offset-4" to={`/studio/${palette.join('-')}`}>Edit this starting palette in Studio</Link>
          <Link className="text-brand-700 underline underline-offset-4" to="/color-palettes">Browse curated palette ideas</Link>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="font-bold">Black or white text on {reference.name}?</h3>
        <div className="mt-3 grid sm:grid-cols-2 gap-4">
          {textChoices.map(choice => (
            <figure key={choice.hex} className="overflow-hidden rounded-xl border border-surface-border">
              <div className="p-5 text-lg font-semibold" style={{ color: `#${choice.hex}`, backgroundColor: `#${reference.hex}` }} aria-hidden="true">Aa — {choice.name} text</div>
              <figcaption className="bg-white p-4 text-sm">
                <span className="block font-semibold">{choice.name} text: {choice.ratio.toFixed(3)}:1</span>
                <span className="mt-1 block text-ink-secondary">Normal-text AA: {choice.ratio >= 4.5 ? 'Pass' : 'Fail'}</span>
                <Link className="mt-2 inline-block text-brand-700 underline underline-offset-4" to={`/contrast-checker?fg=${choice.hex}&bg=${reference.hex}`}>Inspect this text pair</Link>
              </figcaption>
            </figure>
          ))}
        </div>
        <p className="mt-3 text-sm leading-relaxed text-ink-secondary">AA requires at least 4.5:1 for normal text. Results use opaque colors and unrounded contrast values; a passing pair does not establish complete accessibility.</p>
      </div>

      {related.length > 0 && (
        <nav className="mt-6" aria-label={`Comparisons involving ${reference.name}`}>
          <h3 className="font-bold">Compare related names</h3>
          <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-sm">
            {related.map(comparison => <li key={comparison.slug}><Link className="text-brand-700 underline underline-offset-4" to={`/compare/${comparison.slug}`}>{comparison.first.name} vs {comparison.second.name}</Link></li>)}
          </ul>
        </nav>
      )}

      {collection && collectionLink && (
        <section className="mt-6 rounded-xl bg-surface-sunken p-4">
          <h3 className="font-bold">A worked palette in this color family</h3>
          <p className="mt-2 text-sm leading-relaxed text-ink-secondary">{collectionLink.reason}</p>
          <p className="mt-2 text-xs leading-relaxed text-ink-secondary">The collection uses its own listed HEX values; it does not substitute them for this #{reference.hex} reference.</p>
          <Link className="mt-3 inline-block text-sm font-medium text-brand-700 underline underline-offset-4" to={`/color-palettes/${collection.slug}`}>{collection.title}: roles, contrast checks and CSS</Link>
        </section>
      )}

      <div className="mt-6 border-t border-surface-border pt-4">
        <p className="text-sm font-semibold">Sources and methods</p>
        <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-sm">
          <li><a className="text-brand-700 underline underline-offset-4" href="https://www.w3.org/TR/css-color-4/#named-colors">W3C CSS color definitions</a></li>
          <li><a className="text-brand-700 underline underline-offset-4" href="https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html">WCAG text contrast</a></li>
          <li><Link className="text-brand-700 underline underline-offset-4" to="/methodology">Calculation methods</Link></li>
          <li><Link className="text-brand-700 underline underline-offset-4" to="/colors">All color names and HEX codes</Link></li>
        </ul>
      </div>
    </section>
  );
}
